import {
	GridItem,
	Icon
} from "client/Components";
import { HubGridViewLayout } from "client/Features/Hub/HubGridViewLayout/HubGridViewLayout";
import { canDownloadSingleFile } from "client/Features/Documents/SharePoint/actionsRules";
import { useEditorPreference } from "client/Features/Documents/useEditorPreference";
import { getFileDownloadUrl } from "client/Features/Documents/WebDav/actions";
import { HubLinkedDocumentViewModel } from "client/Api/Hub/common";
import {
	AlertManager,
	useAlerts,
	useIsTouchDevice,
	useUnmountedCancellation
} from "client/Hooks";
import {
	generateTrackingId,
	isNil,
	isNotNil
} from "client/Misc/utils";
import { FileThumbnailKind, getDocumentKind } from "client_legacy/Documents/fileExtensions";
import { UserEditorPreference } from "client_legacy/Documents/officeDocumentsOpenPreference";
import { downloadDocument } from "client_legacy/Documents/SharePoint/Actions/downloadAction";
import { Action, ActionKind } from "client_legacy/ndaw/actions";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import { download } from "client_legacy/ndaw/fileDownloader";
import {
	ReactElement,
	useCallback,
	useMemo
} from "react";
import {
	getSharePointOpenActions,
	getWebDavOpenActions,
	guardAgainstInfectedDocuments,
	retrieveSharepointModels,
	retrieveWebDavModels,
	SharePointOpenAction,
	WebDavOpenAction
} from "./documentsActions";
import { WebDavModels } from "client/Api/Documents/WebDav/webDav";
import { documentsConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { getLinkedDocumentId } from "./useAddDocuments";
import { DocumentStoreType } from "client/Api/Documents/common";
import { DocumentItemType } from "client/Api/Documents/SharePoint/common";
import {
	LinkedDocumentsList,
	LinkedDocumentsListMoreButtonText
} from "./LinkedDocumentsList/LinkedDocumentsList";

export type LinkedDocumentsViewerProps = {
	items: readonly HubLinkedDocumentViewModel[];
	onChangeIsLoading(isLoading: boolean): void;
	moreButtonText?: LinkedDocumentsListMoreButtonText;
};

export const LinkedDocumentsViewer = (props: LinkedDocumentsViewerProps): ReactElement => {
	const {
		items,
		onChangeIsLoading,
		moreButtonText
	} = props;

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const editorPreference: UserEditorPreference = useEditorPreference();
	const { webDav: webDavConfiguration, sharePoint: sharePointConfiguration } = documentsConfiguration();
	const isTouch: boolean = useIsTouchDevice();

	const downloadHandler = useCallback(
		async (
			item: HubLinkedDocumentViewModel
		) => {

			switch (item.documentStoreType) {
				case DocumentStoreType.SharePoint:
					const {
						documentItem
					} = await retrieveSharepointModels(
						item.location,
						item.itemId,
						alertManager,
						onChangeIsLoading,
						globalCancellation
					);

					if (isNil(documentItem)) {
						throw new Error(`There is no data for the linked document with id '${item.itemId}'.`);
					}

					if (documentItem.itemType !== DocumentItemType.File) {
						throw new Error(`The linked document with id '${documentItem.id}' is not a file.`);
					}

					if (!canDownloadSingleFile([documentItem])) {
						alertManager.showError(
							localize("_YouDoNotHavePermissionForThat")
						);
						return;
					}

					guardAgainstInfectedDocuments(
						() => {
							downloadDocument(documentItem);
						},
						alertManager
					)(documentItem);

					break;
				case DocumentStoreType.WebDav:
					const webDavModel: WebDavModels = await retrieveWebDavModels(
						item.serverId,
						item.fileId,
						alertManager,
						onChangeIsLoading,
						globalCancellation
					);

					download(
						getFileDownloadUrl(
							webDavModel.item.id,
							webDavModel.server.id
						)
					);
					break;
				default:
					never(item);
			}
		},
		[
			alertManager,
			globalCancellation,
			onChangeIsLoading
		]
	);

	const openHandler = useCallback(
		async (
			item: HubLinkedDocumentViewModel
		) => {

			switch (item.documentStoreType) {
				case DocumentStoreType.SharePoint:
					const {
						documentItem
					} = await retrieveSharepointModels(
						item.location,
						item.itemId,
						alertManager,
						onChangeIsLoading,
						globalCancellation
					);

					if (isNil(documentItem)) {
						throw new Error(`There is no data for the linked document with id '${item.itemId}'.`);
					}

					if (documentItem.itemType !== DocumentItemType.File) {
						throw new Error(`The linked document with id '${documentItem.id}' is not a file.`);
					}

					const sharePointActions: readonly SharePointOpenAction[] = getSharePointOpenActions(
						alertManager,
						editorPreference
					);

					sharePointActions.filter(x => x.canExecute(documentItem))[0]
						?.execute(documentItem);

					break;
				case DocumentStoreType.WebDav:
					if (!webDavConfiguration) {
						throw new Error("WebDav is not available");
					}

					const webDavModel: WebDavModels = await retrieveWebDavModels(
						item.serverId,
						item.fileId,
						alertManager,
						onChangeIsLoading,
						globalCancellation
					);

					if (webDavModel.item.isFolder) {
						throw new Error(`The linked document with id '${webDavModel.item.id}' is not a file.`);
					}

					const webDavActions: readonly WebDavOpenAction[] = getWebDavOpenActions(
						webDavModel.server,
						webDavConfiguration,
						alertManager,
						editorPreference
					);

					webDavActions.filter(x => x.canExecute(webDavModel.item))[0]
						?.execute(webDavModel.item);

					break;
				default:
					never(item);
			}
		},
		[
			alertManager,
			editorPreference,
			globalCancellation,
			onChangeIsLoading,
			webDavConfiguration
		]
	);

	return (
		<LinkedDocumentsList
			trackingId="linked-documents-list"
			moreButtonText={moreButtonText}
		>
			{items.map( x => {
				const id: string = getLinkedDocumentId(x);

				return (
					<HubGridViewLayout.ListItem
						key={id}
					>
						<ListItem
							item={x}
							isTouch={isTouch}
							onOpen={() => openHandler(x)}
							onDownload={() => downloadHandler(x)}
							isWebDavAvailable={isNotNil(webDavConfiguration) && webDavConfiguration.isConfigured}
							isSharePointAvailable={sharePointConfiguration.isConfigured}
						/>
					</HubGridViewLayout.ListItem>
				);
			})}
		</LinkedDocumentsList>
	);
};
LinkedDocumentsViewer.displayName = "LinkedDocumentsViewer";

type ListItemProps = {
	item: HubLinkedDocumentViewModel;
	isTouch: boolean;
	isWebDavAvailable: boolean;
	isSharePointAvailable: boolean;
	onOpen: () => void;
	onDownload: () => void;
};

const ListItem = (props: ListItemProps): ReactElement => {
	const {
		item,
		isWebDavAvailable,
		isSharePointAvailable,
		isTouch,
		onOpen,
		onDownload
	} = props;

	const hasWebDavIssue: boolean = item.documentStoreType === DocumentStoreType.WebDav
		&& !isWebDavAvailable;

	const hasSharePointIssue: boolean = item.documentStoreType === DocumentStoreType.SharePoint
		&& !isSharePointAvailable;

	const isDisabled: boolean = hasWebDavIssue || hasSharePointIssue;

	const tooltip = useMemo(
		(): string | undefined => {
			if (hasSharePointIssue) {
				return localize("_CantOpenBecauseSharePointIsNotConfigured");
			}

			if (hasWebDavIssue) {
				return localize("_CantOpenBecauseWebDavIsNotConfigured");
			}

			return;
		},
		[hasSharePointIssue, hasWebDavIssue]
	);

	const {
		name,
		extension
	} = item;

	const id = useMemo(
		(): string => getLinkedDocumentId(item),
		[item]
	);

	const actions = useMemo(
		(): readonly Action<HubLinkedDocumentViewModel>[] => getActions(
			onOpen,
			onDownload
		),
		[
			onOpen,
			onDownload
		]
	);

	const type: FileThumbnailKind = getDocumentKind(
		DocumentItemType.File,
		extension
	);

	return (
		<GridItem
			{...isDisabled
				? ({
					isDisabled :true
				}) : {
					actions,
					actionContext: item,
					actionsTitle: item.name
				}
			}
			isTouch={isTouch}
			onClick={onOpen}
			trackingId={generateTrackingId("linked-document-item", `|${id}`)}
			tooltip={tooltip}
		>
			<GridItem.Content
				actions={isDisabled
					? undefined
					: actions}
				actionContext={item}
				name={name}
				thumbnail={
					<Icon
						value={{ kind: "svg", name: type }}
					/>
				}
			/>
		</GridItem>
	);
};
ListItem.displayName = "ListItem";

function getActions(
	onOpen: () => void,
	onDownload: () => void
): readonly Action<HubLinkedDocumentViewModel>[] {
	return ([
		{
			kind: ActionKind.Regular,
			name: "open",
			title: localize("_Open"),
			canExecute: _ => true,
			execute: _ => onOpen(),
			icon: "folder-open"
		},
		{
			kind: ActionKind.Regular,
			name: "download",
			title: localize("_Download"),
			canExecute: _ => true,
			execute: _ => onDownload(),
			icon: "download"
		}
	]);
};
